import ConnectionPurposes, { validPurposes } from "./ConnectionPurposes";
import {
  FiArrowLeft,
  FiPlus,
  FiRefreshCw,
  FiMessageCircle,
} from "react-icons/fi";
import { SettingsTabs } from "../../../../partials/content/SettingsNavigation";
import N8nIntegration from "./N8nIntegration";
import { pairConnection } from "./connectionPairing";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Portlet, PortletBody } from "../../../../partials/content/Portlet";
import InputForm from "../../../../components/InputForm";
import { AppAPIService } from "../../../../services/AppAPIService";

const requestDefault = (config) =>
  AppAPIService.getInstance().makeHttpRequest({
    ...config,
    sensitive: true,
    timeout: 15000,
  });
const connectionTypes = [
  { value: "FINANCIAL", label: "Financeiro" },
  { value: "DOCUMENTS", label: "Documentos" },
  { value: "OTHER", label: "Outros" },
];
const states = {
  NOT_CREATED: "Não criada",
  DISCONNECTED: "Desconectada",
  CONNECTING: "Aguardando conexão",
  CONNECTED: "Conectada",
  NUMBER_MISMATCH: "Número diferente do previsto",
  UNAVAILABLE: "Não foi possível conferir",
};
export function validConnection(row) {
  return (
    row &&
    /^[a-z][a-z0-9-]{0,39}$/.test(row.id) &&
    typeof row.label === "string" &&
    row.label.length <= 60 &&
    Object.prototype.hasOwnProperty.call(states, row.state) &&
    row.sendingEnabled === false &&
    typeof row.actionsEnabled === "boolean" &&
    /^•••• \d{4}$/.test(row.expectedNumberMasked)
  );
}
function validStatus(data) {
  return (
    data &&
    data.provider === "EVOLUTION" &&
    data.sendingEnabled === false &&
    typeof data.configured === "boolean" &&
    Array.isArray(data.connections) &&
    data.connections.length <= 20 &&
    (data.nextCursor == null ||
      /^[a-z][a-z0-9-]{0,39}$/.test(data.nextCursor)) &&
    data.connections.every(validConnection) &&
    new Set(data.connections.map((row) => row.id)).size ===
      data.connections.length
  );
}
export default function WhatsappConnectionsPage({ request = requestDefault, embedded = false }) {
  const user = useSelector((state) => state.auth.user);
  const allowed = user?.role === "ADMIN" && Boolean(user?.id);
  const identity = allowed ? String(user.id) : "";
  const epoch = useRef(0);
  const inFlight = useRef(null);
  const qrPanel = useRef(null);
  const [tab, setTab] = useState("connections");
  const [adding, setAdding] = useState(false);
  const [cursor, setCursor] = useState("");
  const [history, setHistory] = useState([]);
  const [connectionType, setConnectionType] = useState(null);
  const [form, setForm] = useState({ label: "", expectedNumber: "" });
  const registration = useRef(null);
  const [revision, setRevision] = useState(0);
  const [purposeRevision, setPurposeRevision] = useState(0);
  const [state, setState] = useState({
    owner: "",
    data: null,
    busy: false,
    error: "",
    qr: null,
  });
  useEffect(() => {
    setConnectionType(null);
    setForm({ label: "", expectedNumber: "" });
    registration.current = null;
    inFlight.current = null;
    setAdding(false);
    setTab("connections");
    setCursor("");
    setHistory([]);
  }, [identity]);
  useEffect(() => {
    const token = ++epoch.current;
    setState({
      owner: identity,
      data: null,
      busy: allowed,
      error: "",
      qr: null,
    });
    if (allowed)
      request({
        method: "GET",
        url:
          "/whatsapp/connections" +
          (cursor ? "?cursor=" + encodeURIComponent(cursor) : ""),
      })
        .then((data) => {
          if (!validStatus(data)) throw Error();
          if (epoch.current === token)
            setState({
              owner: identity,
              data,
              busy: false,
              error: "",
              qr: null,
            });
        })
        .catch(() => {
          if (epoch.current === token)
            setState({
              owner: identity,
              data: null,
              busy: false,
              error:
                "Não foi possível consultar as conexões. Atualize para tentar novamente.",
              qr: null,
            });
        });
    return () => {
      ++epoch.current;
      inFlight.current = null;
    };
  }, [allowed, identity, request, revision, cursor]);
  useEffect(() => {
    if (!state.qr) return undefined;
    const timer = setTimeout(
      () => setState((value) => ({ ...value, qr: null })),
      Math.max(0, Date.parse(state.qr.displayUntil) - Date.now())
    );
    return () => clearTimeout(timer);
  }, [state.qr]);
  useEffect(() => {
    const hide = () => {
      if (document.hidden) {
        ++epoch.current;
        inFlight.current = null;
        setState((value) => ({ ...value, busy: false, qr: null, flow: null }));
      }
    };
    document.addEventListener("visibilitychange", hide);
    return () => document.removeEventListener("visibilitychange", hide);
  }, []);
  useEffect(() => {
    if (
      state.qr &&
      qrPanel.current &&
      typeof qrPanel.current.scrollIntoView === "function"
    ) {
      qrPanel.current.scrollIntoView({ block: "nearest" });
    }
  }, [Boolean(state.qr)]);
  async function connect(connection, token) {
    await pairConnection({
      connection,
      request,
      validConnection,
      current: () => epoch.current === token && !document.hidden,
      update: ({ row, qr, phase }) =>
        setState((value) => ({
          ...value,
          busy: ["creating", "generating"].includes(phase),
          qr,
          flow: { row, phase },
          data: value.data
            ? {
                ...value.data,
                connections: value.data.connections.map((item) =>
                  item.id === row.id ? row : item
                ),
              }
            : value.data,
        })),
    });
  }
  function pairingError(error, token) {
    if (epoch.current !== token) return;
    setState((value) => ({
      ...value,
      busy: false,
      qr: null,
      flow: value.flow ? { ...value.flow, phase: "paused" } : null,
      data: [401, 403].includes(error?.response?.status) ? null : value.data,
      error:
        error?.message === "NUMBER_MISMATCH"
          ? "O número conectado é diferente do informado. Confira o aparelho antes de continuar."
          : "Não foi possível concluir o pareamento. A instância cadastrada foi preservada. Atualize a situação antes de retomar; nenhuma mensagem foi enviada.",
    }));
  }
  async function action(connection) {
    if (
      !allowed ||
      state.owner !== identity ||
      state.busy ||
      inFlight.current !== null ||
      !connection.actionsEnabled
    )
      return;
    const token = ++epoch.current;
    inFlight.current = token;
    setAdding(false);
    setState((value) => ({
      ...value,
      busy: true,
      error: "",
      qr: null,
      flow: { row: connection, phase: "creating" },
    }));
    try {
      await connect(connection, token);
    } catch (error) {
      pairingError(error, token);
    } finally {
      if (inFlight.current === token) inFlight.current = null;
    }
  }
  async function register(event) {
    event.preventDefault();
    if (
      !allowed ||
      state.owner !== identity ||
      state.busy ||
      inFlight.current !== null ||
      !state.data?.registryEnabled ||
      !connectionType ||
      !form.label.trim() ||
      !/^55\d{10,11}$/.test(form.expectedNumber.replace(/[+ ()-]/g, ""))
    )
      return;
    const purpose = connectionType.value === "OTHER" ? null : connectionType.value;
    const payload = JSON.stringify({ ...form, purpose });
    if (
      registration.current?.payload !== payload ||
      registration.current?.owner !== identity
    ) {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      registration.current = {
        payload,
        owner: identity,
        key: Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(""),
      };
    }
    const token = ++epoch.current;
    inFlight.current = token;
    let registered = false;
    let assigningPurpose = false;
    setState((value) => ({
      ...value,
      busy: true,
      error: "",
      qr: null,
      flow: null,
    }));
    try {
      const row = await request({
        method: "POST",
        url: "/whatsapp/connections",
        data: { ...form, idempotencyKey: registration.current.key },
      });
      if (epoch.current !== token) return;
      if (!validConnection(row)) throw Error();
      registered = true;
      registration.current = null;
      setAdding(false);
      setConnectionType(null);
      setForm({ label: "", expectedNumber: "" });
      const refreshed = await request({
        method: "GET",
        url:
          "/whatsapp/connections" +
          (cursor ? "?cursor=" + encodeURIComponent(cursor) : ""),
      });
      if (epoch.current !== token) return;
      if (!validStatus(refreshed)) throw Error();
      setState((value) => ({ ...value, data: refreshed }));
      if (purpose) {
        assigningPurpose = true;
        const url = "/whatsapp/connections/" + row.id + "/purposes";
        const existing = await request({ method: "GET", url });
        if (epoch.current !== token) return;
        if (!validPurposes(existing)) throw Error();
        if (existing.principals[purpose] && existing.principals[purpose] !== row.id)
          throw Error("PURPOSE_ALREADY_ASSIGNED");
        if (!existing.purposes.includes(purpose)) {
          const saved = await request({
            method: "POST", url,
            data: { purposes: [...existing.purposes, purpose], revision: existing.revision },
          });
          if (epoch.current !== token) return;
          if (!validPurposes(saved) || !saved.purposes.includes(purpose) || saved.principals[purpose] !== row.id)
            throw Error();
        }
        setPurposeRevision((value) => value + 1);
        assigningPurpose = false;
      }
      await connect(row, token);
    } catch (error) {
      if (epoch.current !== token) return;
      if (assigningPurpose) {
        setState((value) => ({
          ...value, busy: false, qr: null, flow: null,
          error: error?.message === "PURPOSE_ALREADY_ASSIGNED"
            ? "Instância cadastrada. Já existe uma conta principal para esta finalidade. Use Definir finalidades para conferir e confirmar a substituição antes de conectar."
            : "Instância cadastrada, mas não foi possível confirmar sua finalidade. Use Definir finalidades para conferir e salvar antes de conectar.",
        }));
        setPurposeRevision((value) => value + 1);
        return;
      }
      if (registered) {
        pairingError(error, token);
        return;
      }
      setState((value) => ({
        ...value,
        busy: false,
        data: [401, 403].includes(error?.response?.status) ? null : value.data,
        error:
          "Não foi possível cadastrar. Confira o nome e o número com DDI 55. Se já cadastrou este número, use a conexão existente.",
      }));
    } finally {
      if (inFlight.current === token) inFlight.current = null;
    }
  }
  if (!allowed)
    return <p role="status">Esta área é exclusiva do administrador.</p>;
  const current =
    state.owner === identity
      ? state
      : { data: null, busy: true, error: "", qr: null };
  return (
    <section aria-label="Conexões WhatsApp">
      <div className="xv-settings-toolbar">
        {!embedded && <Link
          className="btn btn-secondary xv-settings-back"
          aria-label="Voltar às parametrizações"
          to="/admin/parametrizacao"
        >
          <FiArrowLeft aria-hidden="true" className="mr-sm-2" />
          <span className="d-none d-sm-inline">Voltar</span>
        </Link>}
        <div className="xv-settings-actions">
          <span className="xv-settings-caption" role="status">
            Envios desligados
          </span>
          {current.data?.environment === "stage" && (
            <span className="xv-settings-state">Homologação</span>
          )}
        </div>
      </div>
      <SettingsTabs
        label="Configurações do WhatsApp"
        disabled={current.busy}
        id="whatsapp"
        value={tab}
        onChange={(next) => {
          setTab(next);
          setAdding(false);
          ++epoch.current;
          inFlight.current = null;
          setState((value) => ({
            ...value,
            busy: false,
            qr: null,
            flow: null,
          }));
        }}
        items={[
          { id: "connections", label: "Conexões" },
          { id: "integrations", label: "Integrações" },
        ]}
      />
      {current.error && (
        <p role="alert" className="xv-settings-note">
          {current.error}
        </p>
      )}
      {current.busy && (
        <p role="status" className="xv-settings-caption mb-3">
          Consultando a conexão…
        </p>
      )}
      <div
        hidden={tab !== "connections"}
        role="tabpanel"
        id="whatsapp-panel-connections"
        aria-labelledby="whatsapp-tab-connections"
      >
        {tab === "connections" && (
          <>
            <div className="xv-settings-toolbar">
              <p className="xv-settings-caption d-none d-sm-block">
                Contas do escritório
              </p>
              <div className="xv-settings-actions ml-auto">
                <button
                  type="button"
                  className="btn btn-secondary xv-settings-back"
                  aria-label="Atualizar conexões"
                  title="Atualizar conexões"
                  disabled={current.busy}
                  onClick={() => setRevision((value) => value + 1)}
                >
                  <FiRefreshCw aria-hidden="true" className="mr-sm-2" />
                  <span className="d-none d-sm-inline">Atualizar</span>
                </button>
                {current.data?.registryEnabled && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    aria-expanded={adding}
                    aria-controls="whatsapp-registration"
                    disabled={
                      current.busy || current.flow?.phase === "scanning"
                    }
                    onClick={() => setAdding((value) => !value)}
                  >
                    <FiPlus aria-hidden="true" className="mr-2" />
                    Nova instância
                  </button>
                )}
              </div>
            </div>
            {current.flow && !current.qr && (
              <Portlet className="mb-3">
                <PortletBody className="kt-portlet__body">
                  <h2 className="h5">Conectar {current.flow.row.label}</h2>
                  <p className="xv-settings-caption" role="status">
                    {
                      {
                        creating: "1 de 3 · Preparando a conexão…",
                        generating: "2 de 3 · Gerando o QR…",
                        scanning:
                          "2 de 3 · Escaneie o QR no WhatsApp do escritório. A confirmação é automática.",
                        connected:
                          "3 de 3 · WhatsApp conectado com o número confirmado. Envios continuam desligados.",
                        blocked:
                          "Instância cadastrada. O pareamento ainda não está liberado neste ambiente.",
                        paused:
                          "Pareamento pausado. Confira a situação para retomar a conexão.",
                      }[current.flow.phase]
                    }
                  </p>
                  {["paused", "blocked"].includes(current.flow.phase) && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={current.busy}
                      onClick={() => {
                        ++epoch.current;
                        inFlight.current = null;
                        setState((value) => ({
                          ...value,
                          flow: null,
                          qr: null,
                        }));
                        setRevision((value) => value + 1);
                      }}
                    >
                      Conferir situação
                    </button>
                  )}
                </PortletBody>
              </Portlet>
            )}
            {current.qr && (
              <Portlet className="mt-3">
                <PortletBody className="kt-portlet__body">
                  <section
                    ref={qrPanel}
                    className="xv-connection-qr"
                    aria-label="Pareamento do WhatsApp"
                  >
                    <h2 className="h5">Conectar {current.qr.label}</h2>
                    <p className="xv-settings-caption mb-3">
                      2 de 3 · No WhatsApp, abra Dispositivos conectados e
                      escaneie o QR. A tela renova o QR e confirma a conexão
                      automaticamente por até dois minutos.
                    </p>
                    <img
                      src={current.qr.image}
                      width="256"
                      height="256"
                      alt="QR temporário para conectar o WhatsApp do escritório"
                    />
                    <button
                      type="button"
                      className="btn btn-secondary mt-3"
                      onClick={() => {
                        ++epoch.current;
                        inFlight.current = null;
                        setState((value) => ({
                          ...value,
                          busy: false,
                          qr: null,
                          flow: value.flow
                            ? { ...value.flow, phase: "paused" }
                            : null,
                        }));
                      }}
                    >
                      Ocultar QR
                    </button>
                  </section>
                </PortletBody>
              </Portlet>
            )}
            {adding && current.data?.registryEnabled && (
              <div className="mb-4">
                <form
                  id="whatsapp-registration"
                  onSubmit={register}
                  aria-label="Cadastrar instância"
                >
                  <h2 className="h5 mb-4">Nova instância</h2>
                  <div className="row">
                    <div className="col-12 col-md-6 mb-3">
                      <InputForm
                        name="whatsapp-connection-type"
                        label="Nome da conexão"
                        placeholder="Selecione a finalidade"
                        isSelect
                        isSearchable={false}
                        isClearable={false}
                        options={connectionTypes}
                        value={connectionType}
                        isDisabled={current.busy}
                        onChange={(option) => {
                          setConnectionType(option);
                          setForm((value) => ({
                            ...value,
                            label: option && option.value !== "OTHER" ? option.label : "",
                          }));
                        }}
                      />
                    </div>
                    {connectionType?.value === "OTHER" && <div className="col-12 col-md-6 mb-3">
                      <InputForm
                        name="whatsapp-label"
                        label="Informe o nome da conexão"
                        value={form.label}
                        isRequired
                        isDisabled={current.busy}
                        onChange={(input) =>
                          setForm((value) => ({
                            ...value,
                            label: input.slice(0, 60),
                          }))
                        }
                      />
                    </div>}
                    <div className="col-12 col-md-6 mb-3">
                      <InputForm
                        name="whatsapp-number"
                        label="WhatsApp com DDI 55"
                        type="tel"
                        value={form.expectedNumber}
                        isRequired
                        isDisabled={current.busy}
                        onChange={(input) =>
                          setForm((value) => ({
                            ...value,
                            expectedNumber: input.slice(0, 24),
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="xv-settings-actions">
                    <button
                      className="btn btn-primary"
                      type="submit"
                      disabled={
                        current.busy ||
                        !connectionType ||
                        !form.label.trim() ||
                        !/^55\d{10,11}$/.test(
                          form.expectedNumber.replace(/[+ ()-]/g, "")
                        )
                      }
                    >
                      Adicionar e conectar
                    </button>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      disabled={current.busy}
                      onClick={() => setAdding(false)}
                    >
                      Cancelar
                    </button>
                  </div>
                  <p className="xv-settings-caption mt-3">
                    Financeiro e Documentos vinculam o número à rotina escolhida.
                    Em Outros, informe um nome para identificar a conta.
                    Após adicionar, o QR aparecerá automaticamente quando o
                    pareamento estiver liberado. Use um número próprio para cada
                    conta.
                  </p>
                </form>
              </div>
            )}
            {current.data && !current.data.configured && (
              <Portlet>
                <PortletBody className="kt-portlet__body">
                  <div className="xv-settings-empty">
                    <FiMessageCircle aria-hidden="true" />
                    <h2 className="h5">WhatsApp em preparação</h2>
                    <p className="xv-settings-caption">
                      O serviço de WhatsApp ainda não foi configurado para este
                      ambiente.
                    </p>
                  </div>
                </PortletBody>
              </Portlet>
            )}
            {current.data?.configured && (
              <>
                {!current.data.connections.length && !current.flow && (
                  <Portlet>
                    <PortletBody className="kt-portlet__body">
                      <div className="xv-settings-empty">
                        <FiMessageCircle aria-hidden="true" />
                        <h2 className="h5">Nenhuma conexão nesta página</h2>
                        <p className="xv-settings-caption">
                          Adicione uma instância para conectar o escritório.
                        </p>
                      </div>
                    </PortletBody>
                  </Portlet>
                )}
                <div className="row" aria-label="Lista de conexões">
                  {current.data.connections.map((row) => (
                    <div className="col-12 col-lg-6" key={row.id}>
                      <Portlet className="xv-settings-card">
                        <PortletBody className="kt-portlet__body">
                          <div className="xv-settings-card__heading">
                            <div>
                              <h2>{row.label}</h2>
                              <p className="xv-settings-caption">
                                {row.expectedNumberMasked}
                              </p>
                            </div>
                          </div>
                          <div className="xv-settings-actions justify-content-between">
                            <span
                              className="xv-settings-state"
                              data-connected={row.state === "CONNECTED"}
                            >
                              {states[row.state]}
                            </span>
                            {row.state === "NOT_CREATED" && (
                              <button
                                type="button"
                                className="btn btn-primary"
                                disabled={
                                  current.busy ||
                                  [
                                    "creating",
                                    "generating",
                                    "scanning",
                                  ].includes(current.flow?.phase) ||
                                  !row.actionsEnabled
                                }
                                onClick={() => action(row, "create")}
                              >
                                Criar instância
                              </button>
                            )}
                            {["DISCONNECTED", "CONNECTING"].includes(
                              row.state
                            ) && (
                              <button
                                type="button"
                                className="btn btn-primary"
                                disabled={
                                  current.busy ||
                                  [
                                    "creating",
                                    "generating",
                                    "scanning",
                                  ].includes(current.flow?.phase) ||
                                  !row.actionsEnabled
                                }
                                onClick={() => action(row, "qr")}
                              >
                                Exibir QR
                              </button>
                            )}
                            {row.state === "NUMBER_MISMATCH" && (
                              <p className="xv-settings-caption">
                                Confira o número conectado antes de continuar.
                              </p>
                            )}
                            {row.state === "CONNECTED" && (
                              <p className="xv-settings-caption">
                                Conta conectada
                              </p>
                            )}
                          </div>
                          <ConnectionPurposes connection={row} request={request} owner={identity} refreshKey={purposeRevision} onSaved={() => setPurposeRevision(value => value + 1)} disabled={current.busy || ["creating", "generating", "scanning"].includes(current.flow?.phase)} />
                        </PortletBody>
                      </Portlet>
                    </div>
                  ))}
                </div>
                {!current.data.connections.every(
                  (row) => row.actionsEnabled
                ) && (
                  <p className="xv-settings-caption mb-3">
                    Gerenciamento ainda não liberado neste ambiente.
                  </p>
                )}
                {(history.length > 0 || current.data.nextCursor) && (
                  <nav
                    className="xv-settings-actions"
                    aria-label="Páginas de conexões"
                  >
                    <button
                      className="btn btn-secondary"
                      disabled={current.busy || !history.length}
                      onClick={() => {
                        setCursor(history[history.length - 1]);
                        setHistory((value) => value.slice(0, -1));
                      }}
                    >
                      Anterior
                    </button>
                    <button
                      className="btn btn-secondary"
                      disabled={current.busy || !current.data.nextCursor}
                      onClick={() => {
                        setHistory((value) => [...value, cursor]);
                        setCursor(current.data.nextCursor);
                      }}
                    >
                      Próxima
                    </button>
                  </nav>
                )}
              </>
            )}
          </>
        )}
      </div>
      <div
        hidden={tab !== "integrations"}
        role="tabpanel"
        id="whatsapp-panel-integrations"
        aria-labelledby="whatsapp-tab-integrations"
      >
        {tab === "integrations" && (
          <Portlet className="xv-settings-card">
            <PortletBody className="kt-portlet__body">
              <N8nIntegration
                request={request}
                identity={identity}
                connections={current.data?.connections || []}
              />
            </PortletBody>
          </Portlet>
        )}
      </div>
    </section>
  );
}
